/**
 * @fileoverview Item Detail Page
 *
 * Displays comprehensive item information including canonical data,
 * AI analysis, sync metadata, and all issue instances with management actions.
 * Can be rendered as a standalone page or within a slide-over panel.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  SparklesIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChartBarIcon,
  DocumentTextIcon,
  TagIcon,
  UserIcon,
  CalendarIcon,
  HashtagIcon,
  VideoCameraIcon,
  SpeakerWaveIcon,
  PhotoIcon,
  CursorArrowRaysIcon,
  QuestionMarkCircleIcon,
  PencilIcon,
  ArchiveBoxIcon,
  NoSymbolIcon,
  EyeIcon,
  CodeBracketIcon,
  FlagIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import { FlagIcon as FlagIconSolid } from '@heroicons/react/24/solid';

// Custom bullseye icon for relevance score with score-based coloring
// Red (< 2.5): outer ring colored, inner ring and dot gray
// Yellow (2.5-3.4): inner ring colored, outer ring and dot gray
// Green (>= 3.5): inner circle filled with green (gray stroke), outer ring gray
const ScoreBullseyeIcon = ({ score, className = 'h-4 w-4' }) => {
  const gray = '#9ca3af'; // gray-400
  const red = '#ef4444';  // red-500
  const yellow = '#eab308'; // yellow-500
  const green = '#22c55e'; // green-500

  let outerColor = gray;
  let innerColor = gray;
  let innerFill = 'none';
  let dotColor = gray;

  if (score < 2.5) {
    outerColor = red;
  } else if (score < 3.5) {
    innerColor = yellow;
  } else {
    innerFill = green;
    dotColor = '#ffffff'; // white dot for definition
  }

  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" stroke={outerColor} />
      <circle cx="12" cy="12" r="6" stroke={innerColor} fill={innerFill} />
      <circle cx="12" cy="12" r="2" fill={dotColor} stroke="none" />
    </svg>
  );
};

import { useItemDetail } from '../hooks/useItemDetail';
import ContentWithAnnotations from '../components/ContentWithAnnotations';
import { useSlideOverStore } from '../store/slideOverStore';
import { useWebSocket } from '../hooks/useWebSocket';
import ToneSelector from '../components/ToneSelector';
import KeyPointSelector from '../components/KeyPointSelector';
import BlurbGeneratorModal from '../components/BlurbGeneratorModal';
import BlurbPromptModal from '../components/BlurbPromptModal';
import { generateSingleBlurb, regenerateBlurb, getBlurbPrompts } from '../api/blurb';
import {
  ITEM_STATUSES,
  ITEM_CATEGORIES,
  INSTANCE_FLAGS,
  getStatusColor,
  getStatusLabel,
  getCategoryColor,
  getCategoryHoverColor,
  getCategoryLabel,
  getFlagOption,
  getFlagColor,
  getFlagBgColor,
} from '../constants/items';

// Keyboard shortcut letters for instance flags (mirrors keydown handler)
const FLAG_SHORTCUTS = { new: 'N', review: 'R', followup: 'F' };

// Type options with icons
const TYPE_OPTIONS = [
  { value: 'article', label: 'Article', icon: DocumentTextIcon },
  { value: 'video', label: 'Video', icon: VideoCameraIcon },
  { value: 'audio', label: 'Audio', icon: SpeakerWaveIcon },
  { value: 'image', label: 'Image', icon: PhotoIcon },
  { value: 'social', label: 'Social', icon: CursorArrowRaysIcon },
  { value: 'unknown', label: 'Unknown', icon: QuestionMarkCircleIcon },
];

const getTypeIcon = (type) => {
  const typeOption = TYPE_OPTIONS.find(option => option.value === type);
  return typeOption ? typeOption.icon : QuestionMarkCircleIcon;
};

/**
 * Get the fill color based on score value
 * Colors: red (1-2.4), yellow (2.5-3.4), green (3.5-5)
 */
const getScoreColor = (scoreValue) => {
  if (scoreValue < 2.5) return 'bg-red-500';
  if (scoreValue < 3.5) return 'bg-yellow-500';
  return 'bg-green-500';
};

/**
 * Quality score icon component - circle stays gray, inner symbol is colored
 * Green check (>= 3.5), yellow exclamation (2.5-3.4), red X (< 2.5)
 */
function QualityScoreIcon({ score, className = 'h-4 w-4' }) {
  const gray = '#9ca3af'; // gray-400
  const red = '#ef4444';  // red-500
  const yellow = '#eab308'; // yellow-500
  const green = '#22c55e'; // green-500

  let symbolColor;
  let symbol;

  if (score >= 3.5) {
    symbolColor = green;
    // Check mark path
    symbol = <path strokeLinecap="round" strokeLinejoin="round" d="M8 13L11 16 16 9" stroke={symbolColor} />;
  } else if (score >= 2.5) {
    symbolColor = yellow;
    // Exclamation mark (line + dot)
    symbol = (
      <>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75" stroke={symbolColor} />
        <circle cx="12" cy="16" r="0.75" fill={symbolColor} stroke="none" />
      </>
    );
  } else {
    symbolColor = red;
    // X mark path
    symbol = <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 7.5l9 9m0-9l-9 9" stroke={symbolColor} />;
  }

  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" stroke={gray} />
      {symbol}
    </svg>
  );
}

/**
 * Score bar component that supports partial fills for decimal scores
 * Each bar can be empty, partially filled, or fully filled
 */
function ScoreBar({ barIndex, score, width = 'w-3.5', height = 'h-2.5' }) {
  // Calculate fill percentage for this bar
  // barIndex is 1-based, so bar 1 fills from score 0-1, bar 2 from 1-2, etc.
  const fillPercent = Math.max(0, Math.min(1, score - (barIndex - 1))) * 100;
  const fillColor = getScoreColor(score);

  return (
    <div className={`${width} ${height} rounded-sm bg-gray-200 dark:bg-gray-700 overflow-hidden`}>
      {fillPercent > 0 && (
        <div
          className={`h-full ${fillColor}`}
          style={{ width: `${fillPercent}%` }}
        />
      )}
    </div>
  );
}

/**
 * Score display component - 5 bar visualization
 * Colors: red (1-2.4), yellow (2.5-3.4), green (3.5-5)
 * Supports decimal scores with partial bar fills
 */
function ScoreDisplay({ score }) {
  if (score == null) return null;

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((bar) => (
        <ScoreBar key={bar} barIndex={bar} score={score} />
      ))}
    </div>
  );
}

/**
 * Dual score display component - shows both Quality and Relevance scores
 * Color coding: red (1-2.4), yellow (2.5-3.4), green (3.5-5)
 * Supports decimal scores with partial bar fills
 */
function DualScoreDisplay({ qualityScore, relevanceScore, showLabels = true }) {
  const renderScore = (score, label) => {
    if (score == null) return null;

    return (
      <div className="flex items-center gap-2">
        {showLabels && <span className="text-xs text-gray-500 dark:text-gray-400 w-20">{label}:</span>}
        <div className="flex items-center gap-0.5" title={`${label}: ${score.toFixed(1)}/5`}>
          {[1, 2, 3, 4, 5].map((bar) => (
            <ScoreBar key={bar} barIndex={bar} score={score} width="w-2.5" height="h-2" />
          ))}
        </div>
        <span className="text-xs text-gray-600 dark:text-gray-300 font-medium">{score.toFixed(1)}</span>
      </div>
    );
  };

  return (
    <div className="space-y-1">
      {renderScore(qualityScore, 'Quality')}
      {renderScore(relevanceScore, 'Relevance')}
    </div>
  );
}

/**
 * Heuristics applied display - badges showing score adjustments
 */
function HeuristicsAppliedDisplay({ heuristics }) {
  if (!heuristics || heuristics.length === 0) return null;

  return (
    <div>
      <label className="block text-xs text-purple-700 dark:text-purple-300 mb-1">Score Adjustments</label>
      <div className="flex flex-wrap gap-1">
        {heuristics.map((h, idx) => (
          <span
            key={idx}
            className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200 rounded"
            title={h.reason}
          >
            {h.type}: {h.adjustment > 0 ? '+' : ''}{h.adjustment?.toFixed(1)} ({h.field})
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Remove instance confirmation dialog with extra friction for published issues
 */
function RemoveInstanceConfirm({ instance, onConfirm, onCancel, isLoading }) {
  const [confirmText, setConfirmText] = useState('');
  const isPublished = instance.issue?.status === 'published';
  const canConfirm = !isPublished || confirmText.toUpperCase() === 'REMOVE';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-2 bg-red-100 dark:bg-red-900/40 rounded-full">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Remove from Issue #{instance.issueNumber}?
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              This will remove this item from the issue. The canonical item will remain in the system.
            </p>

            {isPublished && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-md">
                <p className="text-sm text-red-700 dark:text-red-200 font-medium">
                  Warning: This issue has been published.
                </p>
                <p className="mt-1 text-sm text-red-600 dark:text-red-300">
                  Removing this item may affect analytics and reporting for this issue.
                </p>
                <div className="mt-3">
                  <label className="block text-sm text-gray-700 dark:text-gray-200 mb-1">
                    Type <span className="font-mono font-bold">REMOVE</span> to confirm:
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    className="w-full px-3 py-2 border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-md focus:ring-red-500 focus:border-red-500"
                    placeholder="REMOVE"
                    autoFocus
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!canConfirm || isLoading}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Removing...' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Archive item confirmation dialog
 */
function ArchiveItemConfirm({ item, onConfirm, onCancel, isLoading }) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <div className="bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/40 rounded-full">
            <ArchiveBoxIcon className="h-6 w-6 text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Archive this item?
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              This will archive the item and remove it from review. You can unarchive it later if needed.
            </p>
            <p className="mt-3 text-sm text-amber-700 dark:text-amber-300 font-medium break-words">
              &quot;{item.title || 'Untitled'}&quot;
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-100"
          >
            Cancel
          </button>
          <button
            autoFocus
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Archiving...' : 'Archive'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Block item confirmation dialog
 */
function BlockItemConfirm({ item, onConfirm, onCancel, isLoading }) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <div className="bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-2 bg-gray-200 dark:bg-gray-700 rounded-full">
            <NoSymbolIcon className="h-6 w-6 text-gray-700 dark:text-gray-200" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Block this URL?
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              This will permanently block this URL from being added again. Use this for spam, irrelevant content, or sources you never want to see.
            </p>
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-200 font-medium break-words">
              &quot;{item.title || 'Untitled'}&quot;
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-100"
          >
            Cancel
          </button>
          <button
            autoFocus
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm bg-gray-700 text-white rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Blocking...' : 'Block URL'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Delete item confirmation dialog
 */
function DeleteItemConfirm({ item, onConfirm, onCancel, isLoading }) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <div className="bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-2 bg-red-100 dark:bg-red-900/40 rounded-full">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Delete this item permanently?
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              This will permanently delete this item and remove it from all issues it has been assigned to. This action cannot be undone.
            </p>
            <p className="mt-3 text-sm text-red-700 dark:text-red-300 font-medium break-words">
              &quot;{item.title || 'Untitled'}&quot;
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-100"
          >
            Cancel
          </button>
          <button
            autoFocus
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Deleting...' : 'Delete Permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BlurbOverwriteConfirm({ currentBlurb, newBlurb, onConfirm, onCancel }) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onConfirm();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onCancel, onConfirm]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <div className="bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 p-2 bg-indigo-100 dark:bg-indigo-900/40 rounded-full">
            <ExclamationTriangleIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Overwrite existing blurb?
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              This will replace the current blurb. Press Enter to confirm or Esc to cancel.
            </p>
            <div className="mt-3 space-y-2">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Current</p>
                <p className="text-sm text-gray-700 dark:text-gray-200 line-clamp-3 break-words">
                  {currentBlurb}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Replace with</p>
                <p className="text-sm text-indigo-700 dark:text-indigo-200 line-clamp-3 break-words">
                  {newBlurb}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-100"
          >
            Cancel
          </button>
          <button
            autoFocus
            onClick={onConfirm}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Overwrite
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Add to Issue form
 */
function AddToIssueForm({ onSubmit, isLoading, onCancel }) {
  const [issueNumber, setIssueNumber] = useState('');
  const [category, setCategory] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!issueNumber) return;
    onSubmit({ issueNumber: parseInt(issueNumber), category: category || undefined });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Add to Issue</h4>
      <div className="space-y-3">
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-200 mb-1">Issue Number *</label>
          <input
            type="number"
            value={issueNumber}
            onChange={(e) => setIssueNumber(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
            placeholder="e.g. 42"
            required
            min="1"
          />
        </div>
        <div>
          <label className="block text-sm text-gray-700 dark:text-gray-200 mb-1">Category (optional)</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
          >
            <option value="">Select category...</option>
            {ITEM_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={isLoading || !issueNumber}
            className="flex-1 px-4 py-2 text-sm bg-obn-blue text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Adding...' : 'Add to Issue'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
          >
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

/**
 * Instance row component
 */
function InstanceRow({ instance, onRemove, isRemoving }) {
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const isPublished = instance.issue?.status === 'published';

  const handleRemoveClick = () => {
    setShowRemoveConfirm(true);
  };

  const handleConfirmRemove = async () => {
    await onRemove(instance.id);
    setShowRemoveConfirm(false);
  };

  return (
    <>
      <div className="flex items-center justify-between py-3 px-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to={`/issues/${instance.issueNumber}`}
              className="text-obn-blue dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
            >
              Issue #{instance.issueNumber}
            </Link>
            {instance.category && (
              <span className={`px-2 py-0.5 text-xs rounded border ${getCategoryColor(instance.category)}`}>
                {getCategoryLabel(instance.category)}
              </span>
            )}
            {isPublished ? (
              <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200 rounded">
                Published
              </span>
            ) : (
              <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-200 rounded">
                Draft
              </span>
            )}
          </div>
          {instance.title && instance.title !== instance.url && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 truncate">{instance.title}</p>
          )}
          {instance.issue?.publishedAt && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Published: {new Date(instance.issue.publishedAt).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Statistics placeholder */}
        <div className="flex items-center gap-4 mx-4">
          <div className="text-center">
            <p className="text-xs text-gray-400 dark:text-gray-500">Clicks</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">--</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-400 dark:text-gray-500">Rate</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">--%</p>
          </div>
        </div>

        <button
          onClick={handleRemoveClick}
          disabled={isRemoving}
          className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
          title="Remove from issue"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </div>

      {showRemoveConfirm && (
        <RemoveInstanceConfirm
          instance={instance}
          onConfirm={handleConfirmRemove}
          onCancel={() => setShowRemoveConfirm(false)}
          isLoading={isRemoving}
        />
      )}
    </>
  );
}

/**
 * Main item detail content component
 * @param {string} itemHash - The item hash to display
 * @param {string} [instanceId] - Optional instance ID for issue-specific context
 */
function ItemDetailContent({ itemHash, instanceId = null }) {
  const navigate = useNavigate();
  const { lastMessage } = useWebSocket(null, true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [editContentValue, setEditContentValue] = useState('');
  const [isEditingAuthors, setIsEditingAuthors] = useState(false);
  const [editAuthorsValue, setEditAuthorsValue] = useState('');
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [editUrlValue, setEditUrlValue] = useState('');
  const [isEditingPublished, setIsEditingPublished] = useState(false);
  const [editPublishedValue, setEditPublishedValue] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [pendingBlurb, setPendingBlurb] = useState(null);
  const [instanceDetailsExpanded, setInstanceDetailsExpanded] = useState(true);
  const [instanceCategoryExpanded, setInstanceCategoryExpanded] = useState(false);
  const [hoveredFlagSelector, setHoveredFlagSelector] = useState(false);
  const [aiAnalysisExpanded, setAiAnalysisExpanded] = useState(false);
  const [autoBlurbsExpanded, setAutoBlurbsExpanded] = useState(true); // Will be controlled by effect
  const [generateBlurbExpanded, setGenerateBlurbExpanded] = useState(false); // Always collapsed by default
  // Blurb generation state
  const [showBlurbGenerator, setShowBlurbGenerator] = useState(false);
  const [selectedTone, setSelectedTone] = useState('neutral');
  const [selectedKeyPoints, setSelectedKeyPoints] = useState([]);
  const [blurbAlternatives, setBlurbAlternatives] = useState([]);
  const [isGeneratingBlurb, setIsGeneratingBlurb] = useState(false);
  const [isRegeneratingBlurb, setIsRegeneratingBlurb] = useState(false);
  const [blurbSessionId, setBlurbSessionId] = useState(null);
  const [blurbError, setBlurbError] = useState(null);
  const [blurbReasoning, setBlurbReasoning] = useState('');
  // Blurb prompt modal state
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [blurbPrompts, setBlurbPrompts] = useState(null);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [promptError, setPromptError] = useState(null);
  // Blurb link insertion state
  const [showCustomLinkForm, setShowCustomLinkForm] = useState(false);
  const [customLinkUrl, setCustomLinkUrl] = useState('');
  const blurbTextareaRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const typeDropdownRef = useRef(null);
  const topAnchorRef = useRef(null);
  const instanceCategoryAnchorRef = useRef(null);

  // Close dropdowns on click outside or escape
  useEffect(() => {
    if (!showStatusDropdown && !showCategoryDropdown && !showTypeDropdown && !instanceCategoryExpanded) return;

    const handleClickOutside = (e) => {
      if (showStatusDropdown && statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setShowStatusDropdown(false);
      }
      if (showCategoryDropdown && categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setShowCategoryDropdown(false);
      }
      if (showTypeDropdown && typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
        setShowTypeDropdown(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showStatusDropdown) {
          e.stopPropagation();
          setShowStatusDropdown(false);
        }
        if (showCategoryDropdown) {
          e.stopPropagation();
          setShowCategoryDropdown(false);
        }
        if (showTypeDropdown) {
          e.stopPropagation();
          setShowTypeDropdown(false);
        }
        if (instanceCategoryExpanded) {
          e.stopPropagation();
          setInstanceCategoryExpanded(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showStatusDropdown, showCategoryDropdown, showTypeDropdown, instanceCategoryExpanded]);

  // Scroll category selectors into view when they open (no-op if already visible)
  useEffect(() => {
    if (showCategoryDropdown) {
      topAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showCategoryDropdown]);

  useEffect(() => {
    if (instanceCategoryExpanded) {
      instanceCategoryAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [instanceCategoryExpanded]);

  const {
    item,
    instances,
    publishedInstances,
    unpublishedInstances,
    currentInstance,
    isLoading,
    error,
    updateItem,
    isUpdatingItem,
    assignToIssue,
    isAssigning,
    removeInstance,
    isRemoving,
    updateInstance,
    syncInstanceFromCanonical,
    isSyncingInstance,
    syncToAlgolia,
    isSyncing,
    reprocessAI,
    isReprocessing,
    pendingTaskId,
    processingStartedAt,
    elapsedSeconds,
    isPolling,
    reprocessError,
    cancelReprocess,
    isCancelingReprocess,
    deleteItem,
    isDeleting,
  } = useItemDetail(itemHash, instanceId, lastMessage);

  const { closeSlideOver, navigateNext, navigatePrevious, getPosition, itemList } = useSlideOverStore();

  // Control auto-blurbs section: open if no blurb on instance, closed if there is
  useEffect(() => {
    if (currentInstance) {
      setAutoBlurbsExpanded(!currentInstance.blurb);
    }
  }, [currentInstance?.id, currentInstance?.blurb]);

  // Keyboard shortcuts for item actions
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when a confirmation dialog is open
      if (showArchiveConfirm || showBlockConfirm || showDeleteConfirm || pendingBlurb !== null) return;

      // Don't trigger shortcuts when typing in form fields
      const target = e.target;
      const isFormElement = target.tagName === 'INPUT' ||
                           target.tagName === 'TEXTAREA' ||
                           target.tagName === 'SELECT' ||
                           target.isContentEditable;

      if (isFormElement) return;

      // Don't trigger if any modifier keys are pressed (except shift for uppercase)
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const key = e.key.toUpperCase();

      // Flag toggles (only when we have a current instance)
      if (currentInstance) {
        if (key === 'N') {
          e.preventDefault();
          handleUpdateInstanceField('flag', currentInstance.flag === 'new' ? null : 'new');
          return;
        }
        if (key === 'R') {
          e.preventDefault();
          handleUpdateInstanceField('flag', currentInstance.flag === 'review' ? null : 'review');
          return;
        }
        if (key === 'F') {
          e.preventDefault();
          handleUpdateInstanceField('flag', currentInstance.flag === 'followup' ? null : 'followup');
          return;
        }
      }

      // Item status actions (show confirmation dialogs for destructive, direct action for restore)
      if (key === 'A') {
        e.preventDefault();
        if (item?.status === 'archived') {
          handleUnarchive();
        } else {
          setShowArchiveConfirm(true);
        }
        return;
      }
      if (key === 'B') {
        e.preventDefault();
        if (item?.status === 'blocked') {
          handleUnblock();
        } else {
          setShowBlockConfirm(true);
        }
        return;
      }
      if (key === 'D') {
        e.preventDefault();
        setShowDeleteConfirm(true);
        return;
      }

      // Category selectors: C toggles item category, I toggles instance category
      if (key === 'C' && item) {
        e.preventDefault();
        setShowCategoryDropdown((open) => !open);
        if (instanceCategoryExpanded) setInstanceCategoryExpanded(false);
        return;
      }
      if (key === 'I' && currentInstance) {
        e.preventDefault();
        setInstanceCategoryExpanded((open) => !open);
        if (showCategoryDropdown) setShowCategoryDropdown(false);
        return;
      }

      // Number keys: category selection takes priority when a selector is open
      const numKey = parseInt(e.key, 10);
      if (!isNaN(numKey) && numKey >= 0 && numKey <= 9) {
        if (showCategoryDropdown) {
          e.preventDefault();
          const newCategory = numKey === 0 ? null : ITEM_CATEGORIES[numKey - 1]?.value;
          if (numKey === 0 || newCategory) {
            handleUpdateField('category', newCategory);
            setShowCategoryDropdown(false);
          }
          return;
        }
        if (instanceCategoryExpanded && currentInstance) {
          e.preventDefault();
          const newCategory = numKey === 0 ? null : ITEM_CATEGORIES[numKey - 1]?.value;
          if (numKey === 0 || newCategory) {
            handleUpdateInstanceField('category', newCategory);
            setInstanceCategoryExpanded(false);
          }
          return;
        }
      }

      // Number keys 1-9 for selecting auto-generated blurbs
      const blurbs = item?.ai?.generatedBlurbs || [];
      if (numKey >= 1 && numKey <= blurbs.length && currentInstance) {
        e.preventDefault();
        const selectedBlurb = blurbs[numKey - 1];
        if (!autoBlurbsExpanded) {
          setAutoBlurbsExpanded(true);
        }
        selectBlurb(selectedBlurb.text);
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentInstance, item, autoBlurbsExpanded, showArchiveConfirm, showBlockConfirm, showDeleteConfirm, pendingBlurb, showCategoryDropdown, instanceCategoryExpanded]);

  const handleAddToIssue = async ({ issueNumber, category }) => {
    try {
      await assignToIssue({ issueNumber, category });
      setShowAddForm(false);
    } catch (err) {
      console.error('Failed to add to issue:', err);
      alert(err.response?.data?.error || 'Failed to add to issue');
    }
  };

  const handleUpdateField = async (field, value) => {
    try {
      await updateItem({ [field]: value });
    } catch (err) {
      console.error('Failed to update item:', err);
      alert('Failed to update item');
    }
  };

  const handleContentEdit = () => {
    setEditContentValue(item.content || '');
    setIsEditingContent(true);
  };

  const handleContentSave = async () => {
    setIsEditingContent(false);
    if (editContentValue !== item.content) {
      await handleUpdateField('content', editContentValue);
    }
  };

  const handleContentCancel = () => {
    setEditContentValue(item.content || '');
    setIsEditingContent(false);
  };

  const handleContentKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      handleContentCancel();
    }
  };

  // Format authors array for display: "A", "A and B", or "A, B, and C"
  const formatAuthors = (authors) => {
    if (!authors || authors.length === 0) return '';
    if (authors.length === 1) return authors[0];
    if (authors.length === 2) return `${authors[0]} and ${authors[1]}`;
    return `${authors.slice(0, -1).join(', ')}, and ${authors[authors.length - 1]}`;
  };

  // Parse comma-separated input into authors array
  const parseAuthors = (input) => {
    return input
      .split(',')
      .map((name) => name.trim())
      .filter((name) => name.length > 0);
  };

  const handleAuthorsClick = () => {
    setEditAuthorsValue(item.authors?.join(', ') || '');
    setIsEditingAuthors(true);
  };

  const handleAuthorsBlur = async () => {
    setIsEditingAuthors(false);
    const newAuthors = parseAuthors(editAuthorsValue);
    const currentAuthors = item.authors || [];
    if (JSON.stringify(newAuthors) !== JSON.stringify(currentAuthors)) {
      await handleUpdateField('authors', newAuthors);
    }
  };

  const handleAuthorsKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setEditAuthorsValue(item.authors?.join(', ') || '');
      setIsEditingAuthors(false);
    }
  };

  const handleReprocess = async () => {
    try {
      await reprocessAI();
    } catch (err) {
      console.error('Failed to reprocess:', err);
      alert(err.response?.data?.error || 'Failed to reprocess item');
    }
  };

  const handleCancelReprocess = async () => {
    try {
      await cancelReprocess();
    } catch (err) {
      console.error('Failed to cancel reprocess:', err);
      alert(err.response?.data?.error || 'Failed to cancel reprocessing');
    }
  };

  /**
   * Format elapsed seconds into a human-readable string
   * @param {number} seconds - Elapsed time in seconds
   * @returns {string} Formatted time (e.g., "5s", "1m 23s")
   */
  const formatElapsedTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const handleSync = async () => {
    try {
      await syncToAlgolia();
    } catch (err) {
      console.error('Failed to sync:', err);
      alert('Failed to sync to Algolia');
    }
  };

  const handleArchive = async () => {
    try {
      // Get position before archiving (for navigation after instance removal)
      const position = getPosition();
      const isLastItem = !position || position.total <= 1;
      const isAtEnd = position && position.current === position.total;

      await updateItem({ status: 'archived' });

      // If viewing from instance context, also remove the instance from the issue
      if (currentInstance) {
        await removeInstance(currentInstance.id);

        // Navigate to next/previous item, or close if this was the last one
        if (isLastItem) {
          closeSlideOver();
        } else if (isAtEnd) {
          navigatePrevious();
        } else {
          navigateNext();
        }
      }
    } catch (err) {
      console.error('Failed to archive:', err);
      alert('Failed to archive item');
    }
  };

  const handleUnarchive = async () => {
    try {
      await updateItem({ status: 'review' });
    } catch (err) {
      console.error('Failed to unarchive:', err);
      alert('Failed to unarchive item');
    }
  };

  const handleBlock = async () => {
    try {
      // Get position before blocking (for navigation after instance removal)
      const position = getPosition();
      const isLastItem = !position || position.total <= 1;
      const isAtEnd = position && position.current === position.total;

      await updateItem({ status: 'blocked' });

      // If viewing from instance context, also remove the instance from the issue
      if (currentInstance) {
        await removeInstance(currentInstance.id);

        // Navigate to next/previous item, or close if this was the last one
        if (isLastItem) {
          closeSlideOver();
        } else if (isAtEnd) {
          navigatePrevious();
        } else {
          navigateNext();
        }
      }
    } catch (err) {
      console.error('Failed to block:', err);
      alert('Failed to block item');
    }
  };

  const handleUnblock = async () => {
    try {
      await updateItem({ status: 'review' });
    } catch (err) {
      console.error('Failed to unblock:', err);
      alert('Failed to unblock item');
    }
  };

  const handleMarkForReview = async () => {
    try {
      await updateItem({ status: 'review' });
    } catch (err) {
      console.error('Failed to mark for review:', err);
      alert('Failed to mark item for review');
    }
  };

  const handleDelete = async () => {
    try {
      // Get position before deleting
      const position = getPosition();
      const isLastItem = !position || position.total <= 1;
      const isAtEnd = position && position.current === position.total;

      await deleteItem();
      setShowDeleteConfirm(false);

      // Navigate to next/previous item, or close if this was the last one
      if (isLastItem) {
        closeSlideOver();
        navigate('/items');
      } else if (isAtEnd) {
        // If we're at the end of the list, go to previous
        navigatePrevious();
      } else {
        // Otherwise go to next (which will now be at the same index since current was removed)
        navigateNext();
      }
    } catch (err) {
      console.error('Failed to delete:', err);
      alert('Failed to delete item');
    }
  };

  const handleUpdateInstanceField = async (field, value) => {
    if (!currentInstance) return;
    try {
      await updateInstance({ instanceId: currentInstance.id, updates: { [field]: value } });
    } catch (err) {
      console.error('Failed to update instance:', err);
      alert('Failed to update instance');
    }
  };

  // Blurb link insertion handlers
  const handleInsertArticleLink = () => {
    const textarea = blurbTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selectedText = text.substring(start, end);

    if (selectedText) {
      // Wrap selected text in [[]]
      const newText = text.substring(0, start) + '[[' + selectedText + ']]' + text.substring(end);
      textarea.value = newText;
      // Update the instance
      handleUpdateInstanceField('blurb', newText);
      // Restore focus and set cursor after the inserted link
      textarea.focus();
      const newCursorPos = start + selectedText.length + 4; // +4 for [[ and ]]
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    } else {
      // No selection - insert placeholder
      const placeholder = '[[link text]]';
      const newText = text.substring(0, start) + placeholder + text.substring(end);
      textarea.value = newText;
      handleUpdateInstanceField('blurb', newText);
      // Select the placeholder text for easy replacement
      textarea.focus();
      textarea.setSelectionRange(start + 2, start + 2 + 'link text'.length);
    }
  };

  const handleInsertCustomLink = () => {
    const textarea = blurbTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selectedText = text.substring(start, end);

    if (showCustomLinkForm && customLinkUrl) {
      // Insert the markdown link
      const linkText = selectedText || 'link text';
      const markdownLink = `[${linkText}](${customLinkUrl})`;
      const newText = text.substring(0, start) + markdownLink + text.substring(end);
      textarea.value = newText;
      handleUpdateInstanceField('blurb', newText);
      // Reset form
      setShowCustomLinkForm(false);
      setCustomLinkUrl('');
      textarea.focus();
    } else {
      // Show the URL input form
      setShowCustomLinkForm(true);
    }
  };

  const handleCancelCustomLink = () => {
    setShowCustomLinkForm(false);
    setCustomLinkUrl('');
    blurbTextareaRef.current?.focus();
  };

  // Blurb generation handlers
  const handleGenerateBlurb = async () => {
    if (!currentInstance || !item) return;

    setIsGeneratingBlurb(true);
    setBlurbError(null);
    setBlurbAlternatives([]);
    setShowBlurbGenerator(true);

    try {
      const result = await generateSingleBlurb(itemHash, {
        selectedKeyPoints,
        tone: selectedTone,
        category: currentInstance.category || item.category || 'reads',
      });

      setBlurbAlternatives(result.alternatives || []);
      setBlurbSessionId(result.sessionId);
      setBlurbReasoning(result.reasoning || '');
    } catch (err) {
      console.error('Failed to generate blurb:', err);
      setBlurbError(err.response?.data?.error || err.message || 'Failed to generate blurb');
    } finally {
      setIsGeneratingBlurb(false);
    }
  };

  const handleSelectBlurb = (blurbText) => {
    if (!currentInstance) return;
    handleUpdateInstanceField('blurb', blurbText);
    setShowBlurbGenerator(false);
  };

  const applyBlurb = (blurbText) => {
    if (!currentInstance) return;
    handleUpdateInstanceField('blurb', blurbText);
    if (blurbTextareaRef.current) {
      // Textarea is uncontrolled (defaultValue + key remount), so sync via ref.
      blurbTextareaRef.current.value = blurbText;
      if (document.activeElement === blurbTextareaRef.current) {
        blurbTextareaRef.current.blur();
      }
    }
    if (currentInstance.flag === 'new') {
      handleUpdateInstanceField('flag', null);
    }
  };

  const selectBlurb = (blurbText) => {
    if (!currentInstance) return;
    const existing = (currentInstance.blurb || '').trim();
    if (existing) {
      setPendingBlurb(blurbText);
    } else {
      applyBlurb(blurbText);
    }
  };

  const handleRegenerateBlurb = async (feedback) => {
    if (!blurbSessionId) return;

    setIsRegeneratingBlurb(true);
    setBlurbError(null);

    try {
      const result = await regenerateBlurb(blurbSessionId, feedback, 'single-blurb');

      if (result.regenerated?.alternatives) {
        setBlurbAlternatives(result.regenerated.alternatives);
        setBlurbSessionId(result.sessionId);
        setBlurbReasoning(result.regenerated.reasoning || '');
      }
    } catch (err) {
      console.error('Failed to regenerate blurb:', err);
      setBlurbError(err.response?.data?.error || err.message || 'Failed to regenerate blurb');
    } finally {
      setIsRegeneratingBlurb(false);
    }
  };

  const handleCloseBlurbGenerator = () => {
    setShowBlurbGenerator(false);
    // Don't reset state so user can reopen and see previous results
  };

  // Handler to fetch and show blurb prompts
  const handleShowBlurbPrompts = async () => {
    if (!currentInstance || !item) return;

    setIsLoadingPrompts(true);
    setPromptError(null);
    setShowPromptModal(true);

    try {
      const prompts = await getBlurbPrompts(itemHash, {
        selectedKeyPoints,
        tone: selectedTone,
        category: currentInstance.category || item.category || 'reads',
      });
      setBlurbPrompts(prompts);
    } catch (err) {
      console.error('Failed to fetch blurb prompts:', err);
      setPromptError(err.response?.data?.error || err.message || 'Failed to load prompts');
    } finally {
      setIsLoadingPrompts(false);
    }
  };

  const handleClosePromptModal = () => {
    setShowPromptModal(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-obn-blue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-100">Failed to load item details</p>
          <p className="text-sm text-red-600 dark:text-red-300 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-100">Item not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" ref={topAnchorRef}>
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <textarea
              defaultValue={item.title || ''}
              onBlur={(e) => {
                if (e.target.value !== item.title) {
                  handleUpdateField('title', e.target.value);
                }
              }}
              onChange={(e) => {
                // Remove line breaks
                e.target.value = e.target.value.replace(/[\r\n]/g, ' ');
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                } else if (e.key === 'Escape') {
                  e.stopPropagation();
                  e.target.value = item.title || '';
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                  e.target.blur();
                }
              }}
              className="w-full text-xl font-semibold text-obn-blue dark:text-white bg-transparent outline-none resize-none overflow-hidden"
              placeholder="Untitled"
              rows={1}
              ref={(el) => {
                if (el) {
                  el.style.height = 'auto';
                  el.style.height = el.scrollHeight + 'px';
                }
              }}
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {/* Status dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  className={`px-2 py-0.5 text-xs font-medium rounded border cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-gray-300 dark:hover:ring-gray-600 ${getStatusColor(item.status)}`}
                >
                  {getStatusLabel(item.status)}
                </button>
                {showStatusDropdown && (
                  <div
                    ref={statusDropdownRef}
                    className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 py-1 min-w-[140px]"
                  >
                    {ITEM_STATUSES.map((statusOption) => (
                      <button
                        key={statusOption.value}
                        onClick={() => {
                          if (statusOption.value !== item.status) {
                            handleUpdateField('status', statusOption.value);
                          }
                          setShowStatusDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200 flex items-center gap-2 ${statusOption.value === item.status ? 'font-semibold' : ''}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${statusOption.color.split(' ')[0]}`} />
                        {statusOption.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Category dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  title="Change category (C)"
                  className={`px-2 py-0.5 text-xs font-medium rounded border cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-gray-300 dark:hover:ring-gray-600 ${item.category ? getCategoryColor(item.category) : 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600'}`}
                >
                  {getCategoryLabel(item.category)}
                </button>
                {showCategoryDropdown && (
                  <div
                    ref={categoryDropdownRef}
                    className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 py-1 min-w-[140px]"
                  >
                    <button
                      onClick={() => {
                        if (item.category) {
                          handleUpdateField('category', null);
                        }
                        setShowCategoryDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 ${!item.category ? 'font-semibold' : ''}`}
                    >
                      <span className="font-mono text-gray-400 dark:text-gray-500 w-3 text-right">0</span>
                      <span className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600" />
                      None
                    </button>
                    {ITEM_CATEGORIES.map((cat, idx) => (
                      <button
                        key={cat.value}
                        onClick={() => {
                          if (cat.value !== item.category) {
                            handleUpdateField('category', cat.value);
                          }
                          setShowCategoryDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 dark:text-gray-200 ${cat.hoverColor} ${cat.value === item.category ? 'font-semibold' : ''}`}
                      >
                        <span className="font-mono text-gray-400 dark:text-gray-500 w-3 text-right">{idx + 1}</span>
                        <span className={`w-2 h-2 rounded-full ${cat.color.split(' ')[0]}`} />
                        {cat.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {item.meta?.domain && (
                <span className="text-sm text-gray-500 dark:text-gray-400">{item.meta.domain}</span>
              )}
              <span className="text-sm text-gray-400 dark:text-gray-500">|</span>
              <Link to={`/items/${itemHash}`} className="text-xs font-mono text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">{itemHash}</Link>
              {/* Issue chips - show which issues this item is assigned to */}
              {item.meta?.issues?.length > 0 && (
                <>
                  <span className="text-sm text-gray-400 dark:text-gray-500">|</span>
                  <div className="flex items-center gap-1">
                    {item.meta.issues.map((issueNum) => (
                      <button
                        key={issueNum}
                        onClick={() => {
                          closeSlideOver();
                          navigate(`/issues/${issueNum}`);
                        }}
                        className="px-1.5 py-0.5 text-xs font-mono bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200 border border-blue-200 dark:border-blue-800/50 rounded hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors"
                      >
                        #{issueNum}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* URL with type dropdown */}
        <div className="mt-3 flex items-start gap-1">
          {/* Type dropdown */}
          <div className="relative flex-shrink-0" ref={typeDropdownRef}>
            {(() => {
              const TypeIcon = getTypeIcon(item.type);
              return (
                <button
                  onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  title={`Type: ${TYPE_OPTIONS.find(o => o.value === item.type)?.label || 'Unknown'}`}
                >
                  <TypeIcon className="h-4 w-4" />
                </button>
              );
            })()}
            {showTypeDropdown && (
              <div className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 py-1 min-w-[120px]">
                {TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      if (option.value !== item.type) {
                        handleUpdateField('type', option.value);
                      }
                      setShowTypeDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-200 flex items-center gap-2 ${option.value === item.type ? 'font-semibold bg-blue-50 dark:bg-blue-900/40' : ''}`}
                  >
                    <option.icon className="h-4 w-4" />
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {isEditingUrl ? (
            <input
              type="text"
              value={editUrlValue}
              onChange={(e) => setEditUrlValue(e.target.value)}
              onBlur={() => {
                setIsEditingUrl(false);
                if (editUrlValue !== item.url) {
                  handleUpdateField('url', editUrlValue);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                } else if (e.key === 'Escape') {
                  e.stopPropagation();
                  setEditUrlValue(item.url || '');
                  setIsEditingUrl(false);
                }
              }}
              className="flex-1 text-sm text-obn-blue dark:text-blue-400 bg-transparent outline-none leading-6"
              autoFocus
            />
          ) : (
            <>
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-obn-blue dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 leading-6"
              >
                {item.url}
                <ArrowTopRightOnSquareIcon className="h-3 w-3" />
              </a>
              <button
                onClick={() => {
                  setEditUrlValue(item.url || '');
                  setIsEditingUrl(true);
                }}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                title="Edit URL"
              >
                <PencilIcon className="h-3 w-3" />
              </button>
            </>
          )}
        </div>

        {/* Meta info */}
        <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-300">
          <div className="flex items-center gap-1">
            <span className="p-1">
              <UserIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            </span>
            {isEditingAuthors ? (
              <span className="relative inline-block">
                <span className="invisible whitespace-pre text-sm" aria-hidden="true">
                  {editAuthorsValue || 'Add author...'}
                </span>
                <input
                  type="text"
                  value={editAuthorsValue}
                  onChange={(e) => setEditAuthorsValue(e.target.value)}
                  onBlur={handleAuthorsBlur}
                  onKeyDown={handleAuthorsKeyDown}
                  className="absolute inset-0 text-sm text-gray-600 dark:text-gray-300 bg-transparent outline-none"
                  placeholder="Add author..."
                  autoFocus
                />
              </span>
            ) : (
              <span
                onClick={handleAuthorsClick}
                className="cursor-text hover:text-obn-blue dark:hover:text-blue-400 transition-colors"
                title="Click to edit"
              >
                {formatAuthors(item.authors) || <span className="text-gray-400 dark:text-gray-500">Add author...</span>}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 relative">
            <button
              onClick={() => {
                setEditPublishedValue(item.published ? new Date(item.published).toISOString().split('T')[0] : '');
                setIsEditingPublished(true);
                // Trigger the hidden date input after state update
                setTimeout(() => {
                  const dateInput = document.getElementById('published-date-input');
                  if (dateInput) dateInput.showPicker?.();
                }, 0);
              }}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              title="Edit date"
            >
              <CalendarIcon className="h-4 w-4" />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {item.published ? new Date(item.published).toLocaleDateString() : <span className="text-gray-400 dark:text-gray-500">No date</span>}
            </span>
            {isEditingPublished && (
              <input
                id="published-date-input"
                type="date"
                value={editPublishedValue}
                onChange={(e) => {
                  setEditPublishedValue(e.target.value);
                  const currentDate = item.published ? new Date(item.published).toISOString().split('T')[0] : '';
                  if (e.target.value !== currentDate) {
                    // Use noon UTC to avoid timezone off-by-one issues
                    handleUpdateField('published', e.target.value ? `${e.target.value}T12:00:00.000Z` : null);
                  }
                  setIsEditingPublished(false);
                }}
                onBlur={() => {
                  setIsEditingPublished(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    setIsEditingPublished(false);
                  }
                }}
                className="absolute opacity-0 pointer-events-none"
                autoFocus
              />
            )}
          </div>
          {item.timeToRead != null && (
            <div className="flex items-center gap-1">
              <span className="p-1">
                <ClockIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              </span>
              {item.timeToRead} min read
            </div>
          )}
          {(item.ai?.qualityScore != null || item.ai?.relevanceScore != null) && (
            <div className="flex items-center gap-3">
              {item.ai?.qualityScore != null && (
                <div className="flex items-center gap-1" title={`Quality: ${item.ai.qualityScore.toFixed(1)}/5`}>
                  <QualityScoreIcon score={item.ai.qualityScore} />
                  <ScoreDisplay score={item.ai.qualityScore} />
                </div>
              )}
              {item.ai?.relevanceScore != null && (
                <div className="flex items-center gap-1" title={`Relevance: ${item.ai.relevanceScore.toFixed(1)}/5`}>
                  <ScoreBullseyeIcon score={item.ai.relevanceScore} />
                  <ScoreDisplay score={item.ai.relevanceScore} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Summary - prefers AI summary if available */}
      {(item.ai?.summary || item.summary) && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Summary
            {item.ai?.summary && <span className="ml-1 text-purple-500 dark:text-purple-400">(AI)</span>}
          </label>
          <p className="text-sm text-gray-800 dark:text-gray-200">{item.ai?.summary || item.summary}</p>
        </div>
      )}

      {/* Analysis - AI rationale for scoring */}
      {item.ai?.rationale?.length > 0 && (
        <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4 border border-purple-100 dark:border-purple-800/50">
          <label className="block text-xs font-medium text-purple-700 dark:text-purple-300 mb-2">Analysis</label>
          <ul className="list-disc list-inside space-y-1">
            {item.ai.rationale.map((bullet, idx) => (
              <li key={idx} className="text-sm text-gray-700 dark:text-gray-200">{bullet}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Generated Blurbs Section - Only shown when NOT viewing from an issue */}
      {!currentInstance && item?.ai?.generatedBlurbs?.length > 0 && (
        <div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-lg border border-indigo-200 dark:border-indigo-800/50 overflow-hidden">
          <button
            onClick={() => setAutoBlurbsExpanded(!autoBlurbsExpanded)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <SparklesIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              <span className="font-medium text-indigo-900 dark:text-indigo-100">
                Generated Blurbs ({item.ai.generatedBlurbs.length})
              </span>
              {item.ai.blurbOpinionMatched && (
                <span className="text-xs text-indigo-500 dark:text-indigo-300">
                  Opinion: {item.ai.blurbOpinionSentiment || 'matched'}
                </span>
              )}
            </div>
            <svg
              className={`h-5 w-5 text-indigo-500 dark:text-indigo-300 transition-transform ${autoBlurbsExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {autoBlurbsExpanded && (
            <div className="p-4 space-y-2 bg-white dark:bg-gray-800 border-t border-indigo-200 dark:border-indigo-800/50">
              {item.ai.generatedBlurbs.map((blurb, idx) => {
                const blurbText = typeof blurb === 'string' ? blurb : blurb.text;
                const tone = typeof blurb === 'object' ? blurb.tone : null;
                const confidence = typeof blurb === 'object' ? blurb.confidence : null;
                const opinionAligned = typeof blurb === 'object' ? blurb.opinionAligned : false;
                return (
                  <div
                    key={idx}
                    className={`p-3 text-sm rounded border ${
                      tone === 'positive'
                        ? 'bg-green-50 border-green-200 dark:bg-green-900/30 dark:border-green-800/50'
                        : tone === 'negative'
                          ? 'bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-800/50'
                          : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700'
                    }`}
                  >
                    {tone && (
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                            tone === 'positive'
                              ? 'bg-green-200 text-green-800 dark:bg-green-900/50 dark:text-green-200'
                              : tone === 'negative'
                                ? 'bg-red-200 text-red-800 dark:bg-red-900/50 dark:text-red-200'
                                : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                          }`}
                        >
                          {tone}
                        </span>
                        {confidence && (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            {Math.round(confidence * 100)}% confidence
                          </span>
                        )}
                        {opinionAligned && (
                          <span className="text-[10px] text-purple-500 dark:text-purple-300">opinion-aligned</span>
                        )}
                      </div>
                    )}
                    <p className="text-gray-700 dark:text-gray-200">{blurbText}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Instance Details Section - Only shown when viewing from an issue */}
      {currentInstance && (
        <div className={`rounded-lg border overflow-hidden ${
          currentInstance.flag === 'new' ? 'bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-800/50' :
          currentInstance.flag === 'review' ? 'bg-orange-100 border-orange-300 dark:bg-orange-900/30 dark:border-orange-800/50' :
          currentInstance.flag === 'followup' ? 'bg-purple-100 border-purple-300 dark:bg-purple-900/30 dark:border-purple-800/50' :
          'bg-obn-lightblue border-obn-lightblue dark:bg-blue-900/30 dark:border-blue-800/50'
        }`}>
          <div className={`flex items-center justify-between px-4 py-3 transition-colors ${
            currentInstance.flag === 'new' ? 'hover:bg-green-200 dark:hover:bg-green-900/50' :
            currentInstance.flag === 'review' ? 'hover:bg-orange-200 dark:hover:bg-orange-900/50' :
            currentInstance.flag === 'followup' ? 'hover:bg-purple-200 dark:hover:bg-purple-900/50' :
            'hover:bg-blue-200 dark:hover:bg-blue-900/50'
          }`}>
            <div className="flex items-center gap-2">
              {/* Hoverable Flag Selector */}
              <div
                className="flag-selector relative"
                onMouseEnter={() => setHoveredFlagSelector(true)}
                onMouseLeave={() => setHoveredFlagSelector(false)}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setHoveredFlagSelector(!hoveredFlagSelector);
                  }}
                  className={`p-1 rounded transition-colors ${
                    currentInstance.flag
                      ? getFlagColor(currentInstance.flag)
                      : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                  }`}
                  title={currentInstance.flag ? `Flag: ${getFlagOption(currentInstance.flag)?.label}` : 'Set flag'}
                >
                  {currentInstance.flag ? (
                    <FlagIconSolid className="h-5 w-5" />
                  ) : (
                    <FlagIcon className="h-5 w-5" />
                  )}
                </button>
                {/* Flag selector dropdown on hover */}
                {hoveredFlagSelector && (
                  <div
                    className="absolute z-20 flex items-center gap-1 bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 shadow-lg rounded-md p-1 border border-gray-200 dark:border-gray-700"
                    style={{ animation: 'slideIn 150ms ease-out', left: '-4px', top: '-4px' }}
                  >
                    {INSTANCE_FLAGS.map((flagOpt) => {
                      const shortcut = FLAG_SHORTCUTS[flagOpt.value];
                      return (
                      <button
                        key={flagOpt.value}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUpdateInstanceField('flag', currentInstance.flag === flagOpt.value ? null : flagOpt.value);
                          setHoveredFlagSelector(false);
                        }}
                        className={`px-2 py-1 text-xs font-medium border rounded flex items-center gap-1 transition-all whitespace-nowrap ${
                          currentInstance.flag === flagOpt.value
                            ? `${flagOpt.bgColor} ${flagOpt.color} ${flagOpt.borderColor} ring-2 ring-offset-1 ring-gray-400 dark:ring-gray-500`
                            : `bg-gray-50 text-gray-600 border-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 ${flagOpt.hoverColor}`
                        }`}
                        title={currentInstance.flag === flagOpt.value ? `Clear ${flagOpt.label} flag (${shortcut})` : `Set ${flagOpt.label} flag (${shortcut})`}
                      >
                        {currentInstance.flag === flagOpt.value ? (
                          <FlagIconSolid className={`h-3 w-3 ${flagOpt.color}`} />
                        ) : (
                          <FlagIcon className="h-3 w-3" />
                        )}
                        {flagOpt.label} ({shortcut})
                      </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <button
                onClick={() => setInstanceDetailsExpanded(!instanceDetailsExpanded)}
                className="flex items-center gap-2"
              >
                <span className={`font-medium ${
                  currentInstance.flag === 'new' ? 'text-green-900 dark:text-green-100' :
                  currentInstance.flag === 'review' ? 'text-orange-900 dark:text-orange-100' :
                  currentInstance.flag === 'followup' ? 'text-purple-900 dark:text-purple-100' :
                  'text-obn-blue dark:text-blue-200'
                }`}>
                  Instance Details (Issue #{currentInstance.issueNumber})
                </span>
              </button>
            </div>
            <button
              onClick={() => setInstanceDetailsExpanded(!instanceDetailsExpanded)}
              className="p-1"
            >
              <svg
                className={`h-5 w-5 transition-transform ${
                  currentInstance.flag === 'new' ? 'text-green-500 dark:text-green-300' :
                  currentInstance.flag === 'review' ? 'text-orange-500 dark:text-orange-300' :
                  currentInstance.flag === 'followup' ? 'text-purple-500 dark:text-purple-300' :
                  'text-obn-blue dark:text-blue-300'
                } ${instanceDetailsExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {instanceDetailsExpanded && (
            <div className="px-4 pb-4 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-indigo-700 dark:text-indigo-300">
                  These fields are specific to this issue assignment. Changes here won&apos;t affect other issues or the canonical item.
                </p>
                <button
                  onClick={() => syncInstanceFromCanonical(currentInstance.id)}
                  disabled={isSyncingInstance}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-300 bg-white dark:bg-gray-800 border border-indigo-300 dark:border-indigo-700 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Update this instance with current values from the canonical item (preserves title and category overrides)"
                >
                  <ArrowPathIcon className={`h-3.5 w-3.5 ${isSyncingInstance ? 'animate-spin' : ''}`} />
                  {isSyncingInstance ? 'Syncing...' : 'Sync from Canonical'}
                </button>
              </div>

              {/* Title Override */}
              <div>
                <label className="block text-xs font-medium text-indigo-700 dark:text-indigo-300 mb-1">
                  Title Override
                </label>
                <input
                  key={`title-${currentInstance.id}`}
                  type="text"
                  defaultValue={currentInstance.title || ''}
                  placeholder={item.title || 'No canonical title'}
                  onBlur={(e) => {
                    if (e.target.value !== (currentInstance.title || '')) {
                      handleUpdateInstanceField('title', e.target.value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur();
                    } else if (e.key === 'Escape') {
                      e.stopPropagation();
                      e.target.value = currentInstance.title || '';
                      e.target.blur();
                    }
                  }}
                  className="w-full px-3 py-2 text-sm border border-indigo-200 dark:border-indigo-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-indigo-400 dark:focus:border-indigo-400"
                />
              </div>

              {/* Blurb */}
              <div>
                <label className="block text-xs font-medium text-indigo-700 dark:text-indigo-300 mb-1">
                  Blurb
                </label>
                <textarea
                  ref={blurbTextareaRef}
                  key={`blurb-${currentInstance.id}`}
                  defaultValue={currentInstance.blurb || ''}
                  placeholder="Add a custom blurb for this issue..."
                  onBlur={(e) => {
                    if (e.target.value !== (currentInstance.blurb || '')) {
                      handleUpdateInstanceField('blurb', e.target.value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      e.target.value = currentInstance.blurb || '';
                      e.target.blur();
                    }
                  }}
                  rows={3}
                  id="instance-blurb-textarea"
                  className="w-full px-3 py-2 text-sm border border-indigo-200 dark:border-indigo-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-indigo-400 dark:focus:border-indigo-400 resize-none"
                />

                {/* Blurb link toolbar */}
                <div className="mt-1 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleInsertArticleLink}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/40 border border-indigo-200 dark:border-indigo-800/50 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-colors"
                    title="Wrap selected text in [[]] to link to this article"
                  >
                    <LinkIcon className="h-3 w-3" />
                    Article Link
                  </button>
                  {!showCustomLinkForm ? (
                    <button
                      type="button"
                      onClick={handleInsertCustomLink}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title="Add a custom markdown link [text](url)"
                    >
                      <LinkIcon className="h-3 w-3" />
                      Custom Link
                    </button>
                  ) : (
                    <div className="flex items-center gap-1 flex-1">
                      <input
                        type="url"
                        value={customLinkUrl}
                        onChange={(e) => setCustomLinkUrl(e.target.value)}
                        placeholder="Enter URL..."
                        className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-indigo-400 dark:focus:border-indigo-400"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleInsertCustomLink();
                          } else if (e.key === 'Escape') {
                            e.stopPropagation();
                            handleCancelCustomLink();
                          }
                        }}
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={handleInsertCustomLink}
                        disabled={!customLinkUrl}
                        className="px-2 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Insert
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelCustomLink}
                        className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-auto">
                    [[text]] = article link, [text](url) = custom link
                  </span>
                </div>

                {/* Auto-generated blurbs from initial AI analysis - Collapsible */}
                {item?.ai?.generatedBlurbs?.length > 0 && (
                  <div className="mt-2 border border-indigo-200 dark:border-indigo-800/50 rounded-md overflow-hidden">
                    <button
                      onClick={() => setAutoBlurbsExpanded(!autoBlurbsExpanded)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-indigo-50 dark:bg-indigo-900/40 hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-indigo-700 dark:text-indigo-200">
                          Auto-generated Blurbs ({item.ai.generatedBlurbs.length})
                        </span>
                        {item.ai.blurbOpinionMatched && (
                          <span className="text-[10px] text-indigo-500 dark:text-indigo-300">
                            via {item.ai.blurbOpinionMatched}
                          </span>
                        )}
                      </div>
                      <ChevronRightIcon
                        className={`h-4 w-4 text-indigo-500 dark:text-indigo-300 transition-transform ${autoBlurbsExpanded ? 'rotate-90' : ''}`}
                      />
                    </button>
                    {autoBlurbsExpanded && (
                      <div className="p-2 space-y-1 bg-white dark:bg-gray-800">
                        {item.ai.generatedBlurbs.map((blurb, idx) => (
                          <div key={idx} className="flex gap-1">
                            {/* Numbered handle */}
                            <div
                              className={`flex-shrink-0 w-6 flex items-center justify-center rounded-l border-y border-l text-xs font-mono font-semibold cursor-pointer select-none ${
                                blurb.tone === 'positive'
                                  ? 'bg-green-100 border-green-200 text-green-600 hover:bg-green-200 dark:bg-green-900/40 dark:border-green-800/50 dark:text-green-300 dark:hover:bg-green-900/60'
                                  : blurb.tone === 'negative'
                                    ? 'bg-red-100 border-red-200 text-red-600 hover:bg-red-200 dark:bg-red-900/40 dark:border-red-800/50 dark:text-red-300 dark:hover:bg-red-900/60'
                                    : 'bg-gray-100 border-gray-200 text-gray-500 hover:bg-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                              }`}
                              onClick={() => selectBlurb(blurb.text)}
                              title={`Press ${idx + 1} to select this blurb`}
                            >
                              {idx + 1}
                            </div>
                            <button
                              onClick={() => selectBlurb(blurb.text)}
                              className={`flex-1 text-left p-2 text-xs rounded-r border transition-colors ${
                                blurb.tone === 'positive'
                                  ? 'bg-green-50 border-green-200 hover:bg-green-100 hover:border-green-300 dark:bg-green-900/30 dark:border-green-800/50 dark:hover:bg-green-900/50 dark:hover:border-green-700/50'
                                  : blurb.tone === 'negative'
                                    ? 'bg-red-50 border-red-200 hover:bg-red-100 hover:border-red-300 dark:bg-red-900/30 dark:border-red-800/50 dark:hover:bg-red-900/50 dark:hover:border-red-700/50'
                                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 dark:hover:border-gray-600'
                              }`}
                              title={`Click to use this ${blurb.tone} blurb (or press ${idx + 1})`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span
                                  className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                                    blurb.tone === 'positive'
                                      ? 'bg-green-200 text-green-800 dark:bg-green-900/50 dark:text-green-200'
                                      : blurb.tone === 'negative'
                                        ? 'bg-red-200 text-red-800 dark:bg-red-900/50 dark:text-red-200'
                                        : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                                  }`}
                                >
                                  {blurb.tone}
                                </span>
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                  {Math.round(blurb.confidence * 100)}% confidence
                                </span>
                              </div>
                              <p className="text-gray-700 dark:text-gray-200">{blurb.text}</p>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Generate Blurb Section - Collapsible, closed by default */}
                <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                  <button
                    onClick={() => setGenerateBlurbExpanded(!generateBlurbExpanded)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <SparklesIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
                        Generate Custom Blurb
                      </span>
                    </div>
                    <ChevronRightIcon
                      className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform ${generateBlurbExpanded ? 'rotate-90' : ''}`}
                    />
                  </button>
                  {generateBlurbExpanded && (
                    <div className="p-3 bg-white dark:bg-gray-800 space-y-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleGenerateBlurb}
                          disabled={isGeneratingBlurb}
                          className="flex items-center gap-2 px-3 py-1.5 text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200 rounded-md hover:bg-indigo-200 dark:hover:bg-indigo-900/60 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <SparklesIcon className="h-4 w-4" />
                          {isGeneratingBlurb ? 'Generating...' : 'Generate Blurb'}
                        </button>
                        <button
                          onClick={handleShowBlurbPrompts}
                          disabled={isLoadingPrompts}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="View the prompts that would be sent to the AI"
                        >
                          <CodeBracketIcon className="h-4 w-4" />
                          {isLoadingPrompts ? 'Loading...' : 'Show Prompt'}
                        </button>
                        <ToneSelector
                          value={selectedTone}
                          onChange={setSelectedTone}
                          disabled={isGeneratingBlurb}
                        />
                      </div>

                      {/* Key Points Selection */}
                      {item?.ai?.extracts?.length > 0 && (
                        <KeyPointSelector
                          extracts={item.ai.extracts}
                          selectedPoints={selectedKeyPoints}
                          onSelectionChange={setSelectedKeyPoints}
                          disabled={isGeneratingBlurb}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Category */}
              <div ref={instanceCategoryAnchorRef}>
                <label className="block text-xs font-medium text-indigo-700 dark:text-indigo-300 mb-2">
                  Category
                </label>
                <div className="flex items-center gap-1 flex-wrap">
                  {/* Collapsed: Show only selected category + expand arrow */}
                  {!instanceCategoryExpanded && (
                    <>
                      <button
                        className={`px-3 py-1.5 text-xs font-medium border rounded-md ${
                          currentInstance.category
                            ? getCategoryColor(currentInstance.category)
                            : 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {getCategoryLabel(currentInstance.category)}
                      </button>
                      <button
                        onClick={() => setInstanceCategoryExpanded(true)}
                        className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                        title="Change category (I)"
                      >
                        <ChevronRightIcon className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}

                  {/* Expanded: Show all categories */}
                  {instanceCategoryExpanded && (
                    <>
                      {/* No Category option */}
                      <button
                        onClick={() => {
                          if (currentInstance.category) {
                            handleUpdateInstanceField('category', null);
                          }
                          setInstanceCategoryExpanded(false);
                        }}
                        className={`px-3 py-1.5 text-xs font-medium border rounded-md transition-all bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600
                          ${!currentInstance.category ? 'ring-2 ring-offset-1 ring-gray-400 dark:ring-gray-500' : ''}`}
                      >
                        <span className="font-mono opacity-60 mr-1">0</span>
                        None
                      </button>
                      {ITEM_CATEGORIES.map((cat, idx) => (
                        <button
                          key={cat.value}
                          onClick={() => {
                            if (cat.value !== currentInstance.category) {
                              handleUpdateInstanceField('category', cat.value);
                            }
                            setInstanceCategoryExpanded(false);
                          }}
                          className={`px-3 py-1.5 text-xs font-medium border rounded-md transition-all ${cat.color} ${cat.hoverColor}
                            ${cat.value === currentInstance.category ? 'ring-2 ring-offset-1 ring-gray-400 dark:ring-gray-500' : ''}`}
                        >
                          <span className="font-mono opacity-60 mr-1">{idx + 1}</span>
                          {cat.label}
                        </button>
                      ))}
                      <button
                        onClick={() => setInstanceCategoryExpanded(false)}
                        className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                        title="Collapse"
                      >
                        <ChevronLeftIcon className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Instance Notes */}
              <div>
                <label className="block text-xs font-medium text-indigo-700 dark:text-indigo-300 mb-1">
                  Instance Notes
                </label>
                <textarea
                  key={`notes-${currentInstance.id}`}
                  defaultValue={currentInstance.notes || ''}
                  placeholder="Add notes specific to this issue assignment..."
                  onBlur={(e) => {
                    if (e.target.value !== (currentInstance.notes || '')) {
                      handleUpdateInstanceField('notes', e.target.value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      e.target.value = currentInstance.notes || '';
                      e.target.blur();
                    }
                  }}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-indigo-200 dark:border-indigo-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-indigo-400 dark:focus:border-indigo-400 resize-none"
                />
              </div>
            </div>
          )}

          {/* Blurb Generator Modal */}
          <BlurbGeneratorModal
            isOpen={showBlurbGenerator}
            onClose={handleCloseBlurbGenerator}
            alternatives={blurbAlternatives}
            onSelect={handleSelectBlurb}
            onRegenerate={handleRegenerateBlurb}
            isRegenerating={isRegeneratingBlurb}
            isGenerating={isGeneratingBlurb}
            reasoning={blurbReasoning}
            error={blurbError}
          />

          {/* Blurb Prompt Modal */}
          <BlurbPromptModal
            isOpen={showPromptModal}
            onClose={handleClosePromptModal}
            prompts={blurbPrompts}
            isLoading={isLoadingPrompts}
            error={promptError}
          />
        </div>
      )}

      {/* Action buttons */}
      <div className="space-y-3">
        {/* Processing actions */}
        <div className="flex flex-wrap gap-2">
          {/* AI Reprocess button with elapsed time and cancel option */}
          {isReprocessing ? (
            <div className="flex items-center gap-1">
              <button
                disabled
                className="flex items-center gap-2 px-3 py-2 text-sm bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-200 rounded-md opacity-75"
                title={pendingTaskId ? `Task: ${pendingTaskId}` : ''}
              >
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span>
                  Analyzing...{processingStartedAt && elapsedSeconds > 0 && ` ${formatElapsedTime(elapsedSeconds)}`}
                </span>
              </button>
              <button
                onClick={handleCancelReprocess}
                disabled={isCancelingReprocess}
                className="px-2 py-2 text-sm bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200 rounded-md hover:bg-red-200 dark:hover:bg-red-900/60 disabled:opacity-50"
                title="Cancel processing and allow restart"
              >
                {isCancelingReprocess ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <XMarkIcon className="h-4 w-4" />
                )}
              </button>
            </div>
          ) : (
            <button
              onClick={handleReprocess}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md ${
                reprocessError
                  ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-200 dark:hover:bg-red-900/60'
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/40 dark:text-purple-200 dark:hover:bg-purple-900/60'
              }`}
              title={reprocessError || ''}
            >
              {reprocessError ? (
                <ExclamationTriangleIcon className="h-4 w-4" />
              ) : (
                <SparklesIcon className="h-4 w-4" />
              )}
              {reprocessError ? 'Retry AI' : 'Reprocess AI'}
            </button>
          )}
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200 rounded-md hover:bg-blue-200 dark:hover:bg-blue-900/60 disabled:opacity-50"
          >
            {isSyncing ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : (
              <CloudArrowUpIcon className="h-4 w-4" />
            )}
            {isSyncing ? 'Syncing...' : item.sync?.search?.lastIndexedAt ? 'Resync to Index' : 'Sync to Index'}
          </button>
          <button
            onClick={() => {
              // Open a small popup window for capture
              const width = 300;
              const height = 200;
              const left = (window.screen.width - width) / 2;
              const top = (window.screen.height - height) / 2;
              window.open(
                `${item.url}#obn-ref`,
                'obn-ref',
                `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`
              );
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200 rounded-md hover:bg-green-200 dark:hover:bg-green-900/60"
            title="Opens article in popup window, captures content, then closes automatically"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            {item.meta?.capture?.status === 'captured' ? 'Recapture Content' : 'Capture Content'}
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400 self-center">
            {item.sync?.search?.error ? (
              <span className="text-red-600 dark:text-red-400 flex items-center gap-1">
                <ExclamationTriangleIcon className="h-3 w-3" />
                Sync error
              </span>
            ) : item.sync?.search?.lastIndexedAt ? (
              <>Synced: {new Date(item.sync.search.lastIndexedAt).toLocaleString()}</>
            ) : (
              <span className="text-amber-600 dark:text-amber-400">Not indexed</span>
            )}
          </span>
        </div>

        {/* Status actions */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          {/* Mark for Review - show when not already in review status */}
          {item.status !== 'review' && (
            <button
              onClick={handleMarkForReview}
              disabled={isUpdatingItem}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200 rounded-md hover:bg-orange-200 dark:hover:bg-orange-900/60 disabled:opacity-50"
              title="Mark item for review"
            >
              <EyeIcon className="h-4 w-4" />
              Mark for Review
            </button>
          )}

          {/* Archive / Unarchive */}
          {item.status === 'archived' ? (
            <button
              onClick={handleUnarchive}
              disabled={isUpdatingItem}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
              title="Unarchive item (A)"
            >
              <ArchiveBoxIcon className="h-4 w-4" />
              Unarchive (A)
            </button>
          ) : (
            <button
              onClick={() => setShowArchiveConfirm(true)}
              disabled={isUpdatingItem}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
              title="Archive item (A)"
            >
              <ArchiveBoxIcon className="h-4 w-4" />
              Archive (A)
            </button>
          )}

          {/* Block / Unblock */}
          {item.status === 'blocked' ? (
            <button
              onClick={handleUnblock}
              disabled={isUpdatingItem}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
              title="Unblock item (B)"
            >
              <NoSymbolIcon className="h-4 w-4" />
              Unblock (B)
            </button>
          ) : (
            <button
              onClick={() => setShowBlockConfirm(true)}
              disabled={isUpdatingItem}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
              title="Block this URL permanently (B)"
            >
              <NoSymbolIcon className="h-4 w-4" />
              Block (B)
            </button>
          )}

          {/* Delete */}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200 rounded-md hover:bg-red-200 dark:hover:bg-red-900/60 disabled:opacity-50"
            title="Delete item permanently (D)"
          >
            <TrashIcon className="h-4 w-4" />
            Delete (D)
          </button>
        </div>
      </div>

      {/* Archive confirmation dialog */}
      {showArchiveConfirm && (
        <ArchiveItemConfirm
          item={item}
          onConfirm={async () => {
            await handleArchive();
            setShowArchiveConfirm(false);
          }}
          onCancel={() => setShowArchiveConfirm(false)}
          isLoading={isUpdatingItem}
        />
      )}

      {/* Block confirmation dialog */}
      {showBlockConfirm && (
        <BlockItemConfirm
          item={item}
          onConfirm={async () => {
            await handleBlock();
            setShowBlockConfirm(false);
          }}
          onCancel={() => setShowBlockConfirm(false)}
          isLoading={isUpdatingItem}
        />
      )}

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <DeleteItemConfirm
          item={item}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          isLoading={isDeleting}
        />
      )}

      {/* Blurb overwrite confirmation dialog */}
      {pendingBlurb !== null && (
        <BlurbOverwriteConfirm
          currentBlurb={currentInstance?.blurb || ''}
          newBlurb={pendingBlurb}
          onConfirm={() => {
            applyBlurb(pendingBlurb);
            setPendingBlurb(null);
          }}
          onCancel={() => setPendingBlurb(null)}
        />
      )}

      {/* Notes */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Notes</label>
        <textarea
          defaultValue={item.notes || ''}
          onBlur={(e) => {
            if (e.target.value !== item.notes) {
              handleUpdateField('notes', e.target.value);
            }
          }}
          onChange={(e) => {
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              e.target.value = item.notes || '';
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
              e.target.blur();
            }
          }}
          className="w-full text-sm text-gray-800 dark:text-gray-200 bg-transparent outline-none resize-none overflow-hidden"
          placeholder="Add notes..."
          rows={1}
          ref={(el) => {
            if (el) {
              el.style.height = 'auto';
              el.style.height = el.scrollHeight + 'px';
            }
          }}
        />
      </div>

      {/* Content Preview / Editor */}
      {(item.content || isEditingContent) && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {isEditingContent ? 'Edit Content' : 'Content Preview'}
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ({(isEditingContent ? editContentValue : item.content)?.split(/\s+/).length || 0} words)
              </span>
            </div>
            {isEditingContent ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleContentCancel}
                  className="px-3 py-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleContentSave}
                  className="px-3 py-1 text-sm bg-obn-blue text-white rounded-md hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            ) : (
              <button
                onClick={handleContentEdit}
                className="px-3 py-1 text-sm text-obn-blue dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              >
                Edit
              </button>
            )}
          </div>
          {isEditingContent ? (
            <textarea
              value={editContentValue}
              onChange={(e) => {
                setEditContentValue(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              onKeyDown={handleContentKeyDown}
              className="w-full p-4 text-sm font-mono text-gray-800 dark:text-gray-200 dark:bg-gray-900 border-0 outline-none resize-none min-h-[24rem]"
              placeholder="Enter content in markdown..."
              autoFocus
              ref={(el) => {
                if (el) {
                  el.style.height = 'auto';
                  el.style.height = el.scrollHeight + 'px';
                }
              }}
            />
          ) : (
            <div className="p-4">
              <ContentWithAnnotations
                content={item.content}
                extracts={item.ai?.extracts}
                isEditing={false}
              />
            </div>
          )}
        </div>
      )}

      {/* Issue Assignments Section */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Issue Assignments ({instances.length})
          </h3>
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-obn-blue text-white rounded-md hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4" />
              Add to Issue
            </button>
          )}
        </div>

        <div className="p-4 space-y-4">
          {showAddForm && (
            <AddToIssueForm
              onSubmit={handleAddToIssue}
              isLoading={isAssigning}
              onCancel={() => setShowAddForm(false)}
            />
          )}

          {/* Statistics placeholder */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-dashed border-gray-300 dark:border-gray-600">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-2">
              <ChartBarIcon className="h-5 w-5" />
              <span className="text-sm font-medium">Click Statistics</span>
            </div>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Coming soon: Click tracking will show unique clicks, total clicks, and click rate for
              each issue assignment.
            </p>
          </div>

          {instances.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
              This item has not been assigned to any issues yet.
            </p>
          ) : (
            <div className="space-y-2">
              {/* Unpublished instances first */}
              {unpublishedInstances.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Draft Issues
                  </h4>
                  <div className="space-y-2">
                    {unpublishedInstances.map((instance) => (
                      <InstanceRow
                        key={instance.id}
                        instance={instance}
                        onRemove={removeInstance}
                        isRemoving={isRemoving}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Published instances */}
              {publishedInstances.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    Published Issues
                  </h4>
                  <div className="space-y-2">
                    {publishedInstances.map((instance) => (
                      <InstanceRow
                        key={instance.id}
                        instance={instance}
                        onRemove={removeInstance}
                        isRemoving={isRemoving}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* AI Analysis Section - Collapsible */}
      {item.ai && (item.ai.summary || item.ai.category || item.ai.keywords?.length > 0) && (
        <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-100 dark:border-purple-800/50 overflow-hidden">
          <button
            onClick={() => setAiAnalysisExpanded(!aiAnalysisExpanded)}
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <SparklesIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              <span className="font-medium text-purple-900 dark:text-purple-100">
                AI Analysis
              </span>
              {item.ai.model && (
                <span className="text-xs font-normal text-purple-600 dark:text-purple-300">
                  ({item.ai.model.includes('sonnet') ? 'Sonnet' : 'Haiku'})
                </span>
              )}
            </div>
            <svg
              className={`h-5 w-5 text-purple-500 dark:text-purple-300 transition-transform ${aiAnalysisExpanded ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {aiAnalysisExpanded && (
          <div className="px-4 pb-4 space-y-4">
            {/* Summary */}
            {item.ai.summary && (
              <div>
                <label className="block text-xs text-purple-700 dark:text-purple-300 mb-1">Summary</label>
                <p className="text-sm text-gray-800 dark:text-gray-200">{item.ai.summary}</p>
              </div>
            )}

            {/* Dual Scores */}
            {(item.ai.qualityScore || item.ai.relevanceScore) && (
              <div>
                <label className="block text-xs text-purple-700 dark:text-purple-300 mb-2">Scores</label>
                <DualScoreDisplay
                  qualityScore={item.ai.qualityScore}
                  relevanceScore={item.ai.relevanceScore}
                />
              </div>
            )}

            {/* Category and Keywords row */}
            <div className="grid grid-cols-2 gap-4">
              {item.ai.category && (
                <div>
                  <label className="block text-xs text-purple-700 dark:text-purple-300 mb-1">Category</label>
                  <span className={`inline-block px-2 py-0.5 text-sm rounded border ${getCategoryColor(item.ai.category)}`}>
                    {getCategoryLabel(item.ai.category)}
                  </span>
                </div>
              )}
            </div>

            {/* Keywords */}
            {item.ai.keywords?.length > 0 && (
              <div>
                <label className="block text-xs text-purple-700 dark:text-purple-300 mb-1">Keywords</label>
                <div className="flex flex-wrap gap-1">
                  {item.ai.keywords.map((keyword, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-0.5 text-xs bg-white text-purple-700 dark:bg-gray-800 dark:text-purple-200 rounded border border-purple-200 dark:border-purple-800/50"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Heuristics Applied */}
            <HeuristicsAppliedDisplay heuristics={item.ai.heuristicsApplied} />

            {/* Generated Blurbs */}
            {item.ai.generatedBlurbs?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs text-purple-700 dark:text-purple-300">
                    Generated Blurbs ({item.ai.generatedBlurbs.length})
                  </label>
                  {item.ai.blurbOpinionMatched && (
                    <span className="text-xs text-purple-500 dark:text-purple-300">
                      Opinion: {item.ai.blurbOpinionMatched} ({item.ai.blurbOpinionSentiment})
                    </span>
                  )}
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {item.ai.generatedBlurbs.map((blurb, idx) => (
                    <div
                      key={idx}
                      className={`p-2 text-xs rounded border ${
                        blurb.tone === 'positive'
                          ? 'bg-green-50 border-green-200 dark:bg-green-900/30 dark:border-green-800/50'
                          : blurb.tone === 'negative'
                            ? 'bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-800/50'
                            : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                            blurb.tone === 'positive'
                              ? 'bg-green-200 text-green-800 dark:bg-green-900/50 dark:text-green-200'
                              : blurb.tone === 'negative'
                                ? 'bg-red-200 text-red-800 dark:bg-red-900/50 dark:text-red-200'
                                : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                          }`}
                        >
                          {blurb.tone}
                        </span>
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">
                          {Math.round(blurb.confidence * 100)}% confidence
                        </span>
                        {blurb.opinionAligned && (
                          <span className="text-[10px] text-purple-500 dark:text-purple-300">opinion-aligned</span>
                        )}
                      </div>
                      <p className="text-gray-700 dark:text-gray-200">{blurb.text}</p>
                    </div>
                  ))}
                </div>
                {item.ai.blurbsGeneratedAt && (
                  <p className="text-[10px] text-purple-400 dark:text-purple-300 mt-1">
                    Generated: {new Date(item.ai.blurbsGeneratedAt).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            {/* Metadata footer */}
            <div className="flex gap-4 text-xs text-purple-600 dark:text-purple-300 pt-2 border-t border-purple-200 dark:border-purple-800/50">
              {item.ai.tokensUsed && <span>Tokens: {item.ai.tokensUsed}</span>}
              {item.ai.costUsd && <span>Cost: ${item.ai.costUsd.toFixed(4)}</span>}
              {item.ai.analyzedAt && (
                <span>Analyzed: {new Date(item.ai.analyzedAt).toLocaleString()}</span>
              )}
            </div>
          </div>
          )}
        </div>
      )}

      {/* Item Metadata Section */}
      <ItemMetadataSection item={item} />
    </div>
  );
}

/**
 * Item Metadata Section - displays all meta fields, capture info, and timestamps
 */
function ItemMetadataSection({ item }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!item) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center gap-2">
          <HashtagIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Item Metadata</h3>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">{isExpanded ? '▼' : '▶'}</span>
      </button>

      {isExpanded && (
        <div className="p-4 space-y-4 text-sm">
          {/* Original Summary (from feed) */}
          {item.summary && (
            <div className="space-y-2">
              <h4 className="font-medium text-gray-700 dark:text-gray-200">Original Summary (from feed)</h4>
              <div className="text-xs bg-gray-50 dark:bg-gray-800 rounded p-2 text-gray-700 dark:text-gray-200">
                {item.summary}
              </div>
            </div>
          )}

          {/* Capture Information */}
          {item.meta?.capture && (
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 border border-blue-100 dark:border-blue-800/50">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                <DocumentTextIcon className="h-4 w-4" />
                Content Capture
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-blue-700 dark:text-blue-300">Status:</span>{' '}
                  <span className={`font-medium ${
                    item.meta.capture.status === 'captured' ? 'text-green-700 dark:text-green-300' :
                    item.meta.capture.status === 'pending' ? 'text-yellow-700 dark:text-yellow-300' :
                    item.meta.capture.status === 'failed' ? 'text-red-700 dark:text-red-300' : 'text-gray-700 dark:text-gray-200'
                  }`}>
                    {item.meta.capture.status || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-blue-700 dark:text-blue-300">Source:</span>{' '}
                  <span className="text-gray-800 dark:text-gray-200">{item.meta.capture.source || 'N/A'}</span>
                </div>
                {item.meta.capture.capturedAt && (
                  <div className="col-span-2">
                    <span className="text-blue-700 dark:text-blue-300">Captured At:</span>{' '}
                    <span className="text-gray-800 dark:text-gray-200">{formatDate(item.meta.capture.capturedAt)}</span>
                  </div>
                )}
                {item.meta.capture.needsCapture && (
                  <div className="col-span-2">
                    <span className="text-yellow-700 dark:text-yellow-300">Needs Capture:</span>{' '}
                    <span className="text-gray-800 dark:text-gray-200">{item.meta.capture.captureReason || 'Yes'}</span>
                  </div>
                )}
              </div>

              {/* Readability metadata */}
              {item.meta.capture.readability && (
                <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800/50">
                  <h5 className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-2">Readability Data</h5>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {item.meta.capture.readability.siteName && (
                      <div>
                        <span className="text-blue-700 dark:text-blue-300">Site:</span>{' '}
                        <span className="text-gray-800 dark:text-gray-200">{item.meta.capture.readability.siteName}</span>
                      </div>
                    )}
                    {item.meta.capture.readability.byline && (
                      <div>
                        <span className="text-blue-700 dark:text-blue-300">Byline:</span>{' '}
                        <span className="text-gray-800 dark:text-gray-200">{item.meta.capture.readability.byline}</span>
                      </div>
                    )}
                    {item.meta.capture.readability.length && (
                      <div>
                        <span className="text-blue-700 dark:text-blue-300">Length:</span>{' '}
                        <span className="text-gray-800 dark:text-gray-200">{item.meta.capture.readability.length.toLocaleString()} chars</span>
                      </div>
                    )}
                    {item.meta.capture.readability.lang && (
                      <div>
                        <span className="text-blue-700 dark:text-blue-300">Language:</span>{' '}
                        <span className="text-gray-800 dark:text-gray-200">{item.meta.capture.readability.lang}</span>
                      </div>
                    )}
                    {item.meta.capture.readability.excerpt && (
                      <div className="col-span-2">
                        <span className="text-blue-700 dark:text-blue-300">Excerpt:</span>{' '}
                        <span className="text-gray-800 dark:text-gray-200 italic">"{item.meta.capture.readability.excerpt.substring(0, 100)}..."</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* URL & Domain Info */}
          <div className="space-y-2">
            <h4 className="font-medium text-gray-700 dark:text-gray-200">URL Information</h4>
            <div className="grid grid-cols-1 gap-1 text-xs bg-gray-50 dark:bg-gray-800 rounded p-2">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Hash:</span>{' '}
                <code className="text-gray-800 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 px-1 rounded">{item.hash}</code>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Domain:</span>{' '}
                <span className="text-gray-800 dark:text-gray-200">{item.meta?.domain || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Path:</span>{' '}
                <span className="text-gray-800 dark:text-gray-200 break-all">{item.meta?.path || 'N/A'}</span>
              </div>
              {item.meta?.origUrl && item.meta.origUrl !== item.url && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Original URL:</span>{' '}
                  <span className="text-gray-800 dark:text-gray-200 break-all">{item.meta.origUrl}</span>
                </div>
              )}
              {item.meta?.loginRequired && (
                <div>
                  <span className="text-amber-600 dark:text-amber-400">⚠ Login Required</span>
                </div>
              )}
            </div>
          </div>

          {/* Source (Inoreader) Info */}
          {item.source && (
            <div className="space-y-2">
              <h4 className="font-medium text-gray-700 dark:text-gray-200">Feed Source</h4>
              <div className="grid grid-cols-1 gap-1 text-xs bg-gray-50 dark:bg-gray-800 rounded p-2">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Feed:</span>{' '}
                  <span className="text-gray-800 dark:text-gray-200">{item.source.origin?.title || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Crawled:</span>{' '}
                  <span className="text-gray-800 dark:text-gray-200">{formatDate(item.source.crawlTime)}</span>
                </div>
                {item.source.categories?.length > 0 && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Categories:</span>{' '}
                    <span className="text-gray-800 dark:text-gray-200">{item.source.categories.length} tags</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="space-y-2">
            <h4 className="font-medium text-gray-700 dark:text-gray-200">Timestamps</h4>
            <div className="grid grid-cols-2 gap-2 text-xs bg-gray-50 dark:bg-gray-800 rounded p-2">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Published:</span>{' '}
                <span className="text-gray-800 dark:text-gray-200">{formatDate(item.published)}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Created:</span>{' '}
                <span className="text-gray-800 dark:text-gray-200">{formatDate(item.created)}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Modified:</span>{' '}
                <span className="text-gray-800 dark:text-gray-200">{formatDate(item.modified)}</span>
              </div>
              {item.meta?.archiveUntil && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Archive Until:</span>{' '}
                  <span className="text-gray-800 dark:text-gray-200">{formatDate(item.meta.archiveUntil)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Sync Status */}
          {item.sync?.search && (
            <div className="space-y-2">
              <h4 className="font-medium text-gray-700 dark:text-gray-200">Search Index (Algolia)</h4>
              <div className="grid grid-cols-1 gap-1 text-xs bg-gray-50 dark:bg-gray-800 rounded p-2">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Last Indexed:</span>{' '}
                  <span className="text-gray-800 dark:text-gray-200">{formatDate(item.sync.search.lastIndexedAt)}</span>
                </div>
                {item.sync.search.error && (
                  <div>
                    <span className="text-red-600 dark:text-red-400">Error:</span>{' '}
                    <span className="text-red-800 dark:text-red-200">{item.sync.search.error}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Issue Associations */}
          {item.meta?.issues?.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-gray-700 dark:text-gray-200">Issue Associations</h4>
              <div className="flex flex-wrap gap-1">
                {item.meta.issues.map((issueNum) => (
                  <span
                    key={issueNum}
                    className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 rounded"
                  >
                    #{issueNum}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Main ItemDetailPage component
 *
 * Handles both standalone page rendering and slide-over panel mode.
 * When rendered inside a SlideOverPanel (from ItemsPage/IssuesPage), it uses
 * the itemHash from the store. When accessed directly via URL, it uses the
 * route param.
 */
function ItemDetailPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { isOpen, itemHash, instanceId } = useSlideOverStore();

  // Debounce itemHash and instanceId to prevent rapid API calls during keyboard navigation
  // 200ms delay allows quick navigation without triggering fetches for each item
  const [debouncedItemHash, setDebouncedItemHash] = useState(itemHash);
  const [debouncedInstanceId, setDebouncedInstanceId] = useState(instanceId);

  useEffect(() => {
    // Set immediately if there's no current debounced value (first load)
    if (!debouncedItemHash && itemHash) {
      setDebouncedItemHash(itemHash);
      setDebouncedInstanceId(instanceId);
      return;
    }

    // Debounce subsequent changes
    const timer = setTimeout(() => {
      setDebouncedItemHash(itemHash);
      setDebouncedInstanceId(instanceId);
    }, 200);

    return () => clearTimeout(timer);
  }, [itemHash, instanceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Determine which hash to use - URL param takes precedence for direct navigation
  const activeHash = itemId || itemHash;

  // When rendered inside a SlideOverPanel (no itemId param), just render content
  // The parent component handles the SlideOverPanel wrapper
  // Key prop forces remount when navigating between items, resetting uncontrolled inputs
  // Use debounced hash to avoid API calls during rapid navigation
  if (!itemId && isOpen && itemHash && debouncedItemHash) {
    // Show loading indicator when navigating (position changed but content not yet loaded)
    const isNavigating = itemHash !== debouncedItemHash;

    return (
      <div className="relative">
        {isNavigating && (
          <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 flex items-center justify-center z-10">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <ArrowPathIcon className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          </div>
        )}
        <ItemDetailContent key={debouncedItemHash} itemHash={debouncedItemHash} instanceId={debouncedInstanceId} />
      </div>
    );
  }

  // Standalone page rendering (direct URL access)
  return (
    <div className="max-w-5xl mx-auto">
      {/* Back navigation */}
      <div className="mb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 dark:ring-1 dark:ring-white/10 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <ItemDetailContent itemHash={activeHash} instanceId={null} />
      </div>
    </div>
  );
}

export default ItemDetailPage;
